import React, { useEffect, useState } from 'react';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { Modal } from '../../components/ui/modal';
import type { IntakeSummary } from '../../features/groupOnboarding/api';
import { api, apiEndpoints } from '../../utils/apiService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Platform admin: start an intake link for a group's new location, or for a
 * new restaurant with no group. It can copy any store's answers. Shows the
 * link to copy once made.
 */
export default function NewIntakeLinkModal({
  isOpen,
  onClose,
  onCreated,
  storeOptions,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  storeOptions: SearchableSelectOption[];
}) {
  const [groups, setGroups] = useState<SearchableSelectOption[]>([]);
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [sameEntity, setSameEntity] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [made, setMade] = useState<IntakeSummary | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    api
      .get(apiEndpoints.getStoreGroups())
      .then(async (response) => {
        if (!alive || !response?.ok) return;
        const data = await response.json();
        const list: Array<{ id: number; name: string }> = Array.isArray(data)
          ? data
          : (data?.results ?? []);
        setGroups(list.map((g) => ({ value: String(g.id), label: g.name })));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [isOpen]);

  const close = () => {
    setName('');
    setGroupId('');
    setSourceId('');
    setSameEntity(false);
    setEmail('');
    setError('');
    setMade(null);
    setCopied(false);
    onClose();
  };

  const create = async () => {
    if (!name.trim()) return setError('Name the restaurant or location.');
    if (email.trim() && !EMAIL_RE.test(email.trim()))
      return setError('Enter an email like name@restaurant.com.');
    setSaving(true);
    setError('');
    try {
      const response = await api.post(apiEndpoints.createOnboardingLink(), {
        location_name: name.trim(),
        store_group_id: groupId ? Number(groupId) : null,
        copy_from_store_id: sourceId ? Number(sourceId) : null,
        same_legal_entity: Boolean(sourceId) && sameEntity,
        recipient_email: email.trim(),
      });
      const body = await response?.json().catch(() => null);
      if (!response?.ok) {
        const detail =
          body && typeof body === 'object'
            ? (body.detail ?? Object.values(body)[0])
            : null;
        setError(
          typeof detail === 'string' ? detail : 'Couldn’t create the link.',
        );
        return;
      }
      setMade(body as IntakeSummary);
      onCreated();
    } catch {
      setError('Couldn’t create the link.');
    } finally {
      setSaving(false);
    }
  };

  const sourceName =
    storeOptions.find((o) => String(o.value) === sourceId)?.label ??
    'that store';

  return (
    <Modal isOpen={isOpen} onClose={close} className="max-w-[560px] p-6">
      {made ? (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {made.location_name}’s link is ready
          </h3>
          <div className="flex gap-2">
            <input
              readOnly
              value={made.url}
              aria-label="Intake link"
              onFocus={(e) => e.currentTarget.select()}
              className="app-input min-w-0 flex-1 rounded-lg px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard
                  ?.writeText(made.url)
                  .catch(() => null);
                setCopied(true);
              }}
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {made.recipient_email && (
            <p className="text-sm text-gray-500">
              Emailed to {made.recipient_email}.
            </p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={close}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            New intake link
          </h3>
          <div>
            <Label>Restaurant or location name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Harbour · Southport"
            />
          </div>
          <div>
            <Label>Group</Label>
            <SearchableSelect
              options={[{ value: '', label: 'No group' }, ...groups]}
              value={groupId}
              onChange={setGroupId}
              placeholder="No group"
            />
            <p className="mt-1 text-xs text-gray-500">
              Choose “No group” for a new restaurant.
            </p>
          </div>
          <div>
            <Label>Copy answers from</Label>
            <SearchableSelect
              options={[{ value: '', label: 'Start blank' }, ...storeOptions]}
              value={sourceId}
              onChange={setSourceId}
              placeholder="Start blank"
            />
            {sourceId && (
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={sameEntity}
                  onChange={(e) => setSameEntity(e.target.checked)}
                />
                Same legal entity as {sourceName}
              </label>
            )}
          </div>
          <div>
            <Label>Email the link to (optional)</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="manager@restaurant.com"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-error-600">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={close}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={create}
              disabled={saving}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {saving ? 'Creating…' : 'Create link'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
